import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Guid } from '../types/guid';
import { Worker } from '../types/worker';
import { MoveMachineOrder } from '../types/api/moveMachineOrder';
import { addAuthHeaders } from '../api';
import { MachineCategory } from '../types/machineCategory';
import { DateStr, DateTime } from '../types/util/dateTime';
import {
  NewMachineChangeOrder,
  NewMachineOrder,
} from '../types/api/newMachineOrder';
import { Assignment } from '../types/assignment';
import { config } from '../config/machine';
import { ApproveOrder } from '../types/api/approveOrder';
import { MachineProject } from '../types/machineProject';
import { Machine } from '../types/machine';
import { MoveMachineAssignment } from '../types/api/moveMachineAssignment';
import { Absence } from '../types/absence';
import { Organization } from '../types/organization';
import { SubstituteDriver } from '../types/substituteDriver';
import { NewSubstituteDriver } from '../types/api/newSubstituteDriver';
import { Unavailability } from '../types/unavailability';
import { NewUnavailability } from '../types/api/newUnavailability';
import { Certification } from '../types/certification';
import { UpdateAbsence } from '../types/api/updateAbsence';
import { Roles } from '../types/enums/machineRoles';
import { SecurityTraining } from '../types/securityTraining';
import { Company } from '../types/company';

// Define a service using a base URL and expected endpoints
export const machineApi = createApi({
  reducerPath: 'machine',
  baseQuery: fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'machines',
    'projects',
    'workers',
    'assignments',
    'substitutes',
    'unavailabilities',
    'absences',
    'myCompany',
    'organizations',
    'categories',
  ],
  endpoints: (builder) => ({
    getCategories: builder.query<
      MachineCategory[],
      void | { includeSubCategories?: boolean }
    >({
      query: ({ includeSubCategories } = { includeSubCategories: false }) => ({
        url: '/categories',
        params: {
          includeSubCategories,
        },
      }),
      providesTags: ['categories'],
    }),
    putApproveOrderBody: builder.mutation<
      Assignment,
      { id: string; body: ApproveOrder }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}/assign`,
        method: 'PUT',
        body,
      }),
      // invalidates assignments tag because this endpoint can change assignments
      invalidatesTags: ['assignments'],
    }),
    postMachineOrder: builder.mutation<
      Assignment,
      NewMachineOrder | NewMachineChangeOrder
    >({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    putMoveMachineOrder: builder.mutation<
      Assignment,
      { id: string; body: MoveMachineOrder }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    deleteOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `/orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['assignments'],
    }),
    getProject: builder.query<MachineProject, number>({
      query: (id) => `/projects/${id}`,
      providesTags: ['projects'],
    }),

    getAllProjects: builder.query<MachineProject[], void | { q?: string, organizations?: number[] }>({
      query: (args) => ({
        url: '/projects',
        params: {
          q: args?.q,
          organizations: args?.organizations,
        },
      }),
      providesTags: ['projects'],
    }),
    getWorker: builder.query<Worker, number>({
      query: (id) => `/workers/${id}`,
    }),
    getAllCertificationsWorker: builder.query<Certification[], number>({
      query: (id) => `/workers/${id}/certifications`,
    }),
    getAllDocumentedSecurityTraining: builder.query<SecurityTraining[], number>({
      query: (id) => `/workers/${id}/securityTraining`,
    }),
    getAllWorkers: builder.query<
      Worker[],
      void | { q?: string; email?: string; activeOnly?: boolean }
    >({
      query: ({ q, email, activeOnly } = { activeOnly: true }) => ({
        url: '/workers',
        params: {
          q,
          email,
          activeOnly,
        },
      }),
      providesTags: ['workers'],
    }),
    getAllAbsences: builder.query<Absence[], { from: DateTime; to: DateTime }>({
      query: (params) => ({
        url: '/workers/absences',
        params,
      }),
      providesTags: ['absences'],
    }),

    putAbsence: builder.mutation<
      Absence,
      { workerId: number; id: number; body: UpdateAbsence }
    >({
      query: ({ workerId, id, body }) => ({
        url: `/workers/${workerId}/absences/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['absences'],
    }),

    postAbsence: builder.mutation<Absence, { id: number; body: UpdateAbsence }>(
      {
        query: ({ id, body }) => ({
          url: `/workers/${id}/absences`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['absences'],
      },
    ),

    getAbsence: builder.query<Absence, { workerId: number; id: number }>({
      query: ({ workerId, id }) => ({
        url: `/workers/${workerId}/absences/${id}`,
      }),
      providesTags: ['absences'],
    }),

    deleteAbsence: builder.mutation<Absence, { workerId: number; id: number }>({
      query: ({ workerId, id }) => ({
        url: `/workers/${workerId}/absences/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['absences'],
    }),

    getAllMachines: builder.query<Machine[], void>({
      query: () => '/machines',
      providesTags: ['machines', 'substitutes'],
    }),

    getMachine: builder.query<Machine, number|string>({
      query: (id) => `/machines/${id}`,
      providesTags: ['machines', 'substitutes'],
    }),
    putMachineComment: builder.mutation<Machine, { id: number|string; comment: string; }
    >({
      query: ({ id, comment }) => ({
        url: `/machines/${id}/comment`,
        method: 'PUT',
        params: {
          comment,
        },
      }),
      invalidatesTags: ['machines'],
    }),
    // TODO: assignments
    getAllMachineAssignments: builder.query<
      Assignment[],
      { organizations?: number[]; from: DateStr; to: DateStr, filterOrders?: boolean, internalNumber?: string }
    >({
      query: ({
        organizations, from, to, filterOrders, internalNumber,
      }) => ({
        url: '/assignments',
        params: {
          organizations:
            organizations && organizations.length > 0
              ? organizations.join(',')
              : undefined,
          from,
          to,
          filterOrders,
          internalNumber,
        },
      }),
      providesTags: ['assignments'],
    }),
    putApproveAssignmentBody: builder.mutation<
      Assignment,
      { id: string; body: MoveMachineAssignment }
    >({
      query: ({ id, body }) => ({
        url: `/assignments/${id}/approve`,
        method: 'PUT',
        body,
      }),
      // invalidates assignments tag because this endpoint can change assignments
      invalidatesTags: ['assignments'],
    }),
    putMoveMachineAssignment: builder.mutation<
      Assignment,
      { id: string; body: MoveMachineAssignment }
    >({
      query: ({ id, body }) => ({
        url: `/assignments/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    deleteAssignment: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assignments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['assignments'],
    }),
    deleteAllFutureAssignmentsForProject: builder.mutation<Assignment[], string>({
      query: (id) => ({
        url: `/assignments/${id}`,
        method: 'DELETE',
        params: { deleteAllFutureForProject: true },
      }),
      invalidatesTags: ['assignments'],
    }),
    postCopyAssignment: builder.mutation<Assignment, string>({
      query: (id) => ({
        url: `/assignments/${id}/copy`,
        method: 'POST',
      }),
      invalidatesTags: ['assignments'],
    }),
    postCreateAssignment: builder.mutation<Assignment, MoveMachineAssignment>({
      query: (body) => ({
        url: '/assignments',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    getOrganizations: builder.query<Organization[], void>({
      query: () => '/organizations',
      providesTags: ['organizations'],
    }),
    getMyOrganization: builder.query<Organization, void>({
      query: () => '/organizations/me',
      providesTags: ['organizations'],
    }),
    getMyRoles: builder.query<Roles[], void>({
      query: () => '/organizations/me/roles',
    }),
    getSubstitute: builder.query<
      SubstituteDriver,
      { machineId: string; subId: Guid }
    >({
      query: ({ machineId, subId }) => ({
        url: `/machines/${machineId}/substitutes/${subId}`,
      }),
      providesTags: ['substitutes'],
    }),
    getSubstitutes: builder.query<
      SubstituteDriver[],
      {
        id: string;
        start?: string | undefined;
        end?: string | undefined;
      }
    >({
      query: ({ id, start, end }) => ({
        url: `/machines/${id}/substitutes`,
        params: {
          start,
          end,
        },
      }),
      providesTags: ['substitutes'],
    }),
    getSearchSubstitutes: builder.query<
      SubstituteDriver[],
      {
        id: string;
        q: string;
        limit?: number;
      }
    >({
      query: ({ id, q, limit }) => ({
        url: `/machines/${id}/substitutes/search`,
        params: {
          q,
          limit,
        },
      }),
      providesTags: ['substitutes'],
    }),
    putSubstitute: builder.mutation<
      SubstituteDriver,
      {
        machineId: string;
        subId: Guid;
        body: NewSubstituteDriver;
      }
    >({
      query: ({ machineId, subId, body }) => ({
        url: `/machines/${machineId}/substitutes/${subId}`,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['substitutes', 'machines'],
    }),
    postSubstitute: builder.mutation<
      void,
      { id: string; body: NewSubstituteDriver }
    >({
      query: ({ id, body }) => ({
        url: `/machines/${id}/substitutes`,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['substitutes', 'machines'],
    }),
    deleteSubstitute: builder.mutation<
      SubstituteDriver,
      { machineId: string; subId: Guid }
    >({
      query: ({ machineId, subId }) => ({
        url: `/machines/${machineId}/substitutes/${subId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['substitutes', 'machines'],
    }),
    getUnavailability: builder.query<
      Unavailability,
      { machineId: string; id: Guid }
    >({
      query: ({ machineId, id }) => ({
        url: `/machines/${machineId}/unavailabilities/${id}`,
      }),
      providesTags: ['unavailabilities'],
    }),
    getUnavailabilities: builder.query<
      Unavailability[],
      {
        id: string;
        start?: string | undefined;
        end?: string | undefined;
      }
    >({
      query: ({ id, start, end }) => ({
        url: `/machines/${id}/unavailabilities`,
        params: {
          start,
          end,
        },
      }),
      providesTags: ['unavailabilities'],
    }),
    getSearchunavailabilities: builder.query<
      Unavailability[],
      {
        id: string;
        q: string;
        limit?: number;
      }
    >({
      query: ({ id, q, limit }) => ({
        url: `/machines/${id}/unavailabilities/search`,
        params: {
          q,
          limit,
        },
      }),
      providesTags: ['unavailabilities'],
    }),
    putUnavailability: builder.mutation<
      Unavailability,
      {
        machineId: string;
        id: Guid;
        body: NewUnavailability;
      }
    >({
      query: ({ machineId, id, body }) => ({
        url: `/machines/${machineId}/unavailabilities/${id}`,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['unavailabilities'],
    }),
    postUnavailability: builder.mutation<
      void,
      { id: string; body: NewUnavailability }
    >({
      query: ({ id, body }) => ({
        url: `/machines/${id}/unavailabilities`,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['unavailabilities'],
    }),
    deleteUnavailability: builder.mutation<
      Unavailability,
      { machineId: string; id: Guid }
    >({
      query: ({ machineId, id }) => ({
        url: `/machines/${machineId}/unavailabilities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['unavailabilities'],
    }),

    getAllUnavailabilities: builder.query<
      Unavailability[],
      { start?: DateTime; end?: DateTime } | void
    >({
      query: ({ start, end } = {}) => ({
        url: '/machines/unavailabilities',
        params: {
          start,
          end,
        },
      }),
      providesTags: ['unavailabilities'],
    }),
    getMyCompany: builder.query<Company, void>({
      query: () => '/me/company',
      providesTags: ['myCompany'],
    }),
    setMyCompany: builder.mutation<string, string>({
      query: (companyName) => ({
        url: '/me/company',
        method: 'PUT',
        body: { companyName },
        headers: { 'Content-Type': 'application/json' },
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
          }
          return response.text();
        },
      }),
      invalidatesTags: [
        'myCompany',
        'machines',
        'projects',
        'workers',
        'assignments',
        'substitutes',
        'unavailabilities',
        'absences',
        'organizations',
        'categories',
      ],
    }),
    getCompanyList: builder.query<string[], void>({
      query: () => '/companies',
      providesTags: ['myCompany'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCategoriesQuery,
  usePutApproveOrderBodyMutation,
  usePostMachineOrderMutation,
  usePutMoveMachineOrderMutation,
  useDeleteOrderMutation,
  useGetProjectQuery,
  useGetAllProjectsQuery,
  useGetWorkerQuery,
  useGetAllCertificationsWorkerQuery,
  useGetAllDocumentedSecurityTrainingQuery,
  useGetAllWorkersQuery,
  useGetAllAbsencesQuery,
  usePostAbsenceMutation,
  usePutAbsenceMutation,
  useGetAbsenceQuery,
  useDeleteAbsenceMutation,
  useGetAllMachinesQuery,
  useGetMachineQuery,
  usePutMachineCommentMutation,
  useGetAllMachineAssignmentsQuery,
  usePutApproveAssignmentBodyMutation,
  usePutMoveMachineAssignmentMutation,
  useDeleteAssignmentMutation,
  useDeleteAllFutureAssignmentsForProjectMutation,
  usePostCopyAssignmentMutation,
  usePostCreateAssignmentMutation,
  useGetOrganizationsQuery,
  useGetMyOrganizationQuery,
  useGetMyRolesQuery,
  useGetSubstituteQuery,
  useGetSubstitutesQuery,
  usePutSubstituteMutation,
  usePostSubstituteMutation,
  useDeleteSubstituteMutation,
  useGetSearchSubstitutesQuery,
  useGetUnavailabilityQuery,
  useGetUnavailabilitiesQuery,
  usePutUnavailabilityMutation,
  usePostUnavailabilityMutation,
  useDeleteUnavailabilityMutation,
  useGetSearchunavailabilitiesQuery,
  useGetAllUnavailabilitiesQuery,
  useGetMyCompanyQuery,
  useSetMyCompanyMutation,
  useGetCompanyListQuery,
  util,
} = machineApi;

export default machineApi.reducer;
